/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ShiftBusyTime, UserBusyTimes } from "@microsoft/shifts-models-data";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { InstrumentationService } from "sh-services";
import { AppDb } from "sh-services/data/AppDb";

import { CrossLocationShiftDatabaseParams } from "./CrossLocationShiftDatabaseParams";
import { CrossLocationShiftDbEntity } from "./CrossLocationShiftDbEntity";
import { ICrossLocationShiftDatabase } from "./ICrossLocationShiftDatabase";

// TODO: After fixing circular dependency, move 'ICrossLocationShiftDatabase' to managers package under 'crossLocationShift' folder.
/**
 * The cross-location shift database.
 * It stores for a given home team members all cross-location shifts assigned to them within same team schedule group during a date-time interval.
 */
export class CrossLocationShiftDatabase implements ICrossLocationShiftDatabase {
    /**
     * Instantiates a new instance of the CrossLocationShiftDatabase.
     * @param database The database.
     */
    constructor(private readonly database: AppDb) {}

    /**
     * Gets the cross-location shifts assigned to the given home team members during given date-time interval.
     * @param params The parameters.
     * @returns The cross-location shifts assigned to the given home team members during given date-time interval or undefined if no data was found.
     */
    public async getCrossLocationShifts(params: CrossLocationShiftDatabaseParams): Promise<UserBusyTimes[] | undefined> {
        let shifts: UserBusyTimes[] | undefined;

        try {
            const { endTimestampMs, startTimestampMs, teamId } = params;

            const records = await this.database.crossLocationShifts
                .where({ teamId })
                .and(record => this.isRecordInInterval(startTimestampMs, endTimestampMs, record))
                .toArray();

            if (records.length > 0) {
                const shiftsByUsers = this.createShiftsByUsers(records);
                const usersIds = Object.keys(shiftsByUsers);

                shifts = usersIds.map(userId => ({
                    busyTime: shiftsByUsers[userId],
                    tenantId: "",
                    userId
                }));
            }
        } catch (error) {
            InstrumentationService.trackException(error, "CrossLocationShiftDatabase.getCrossLocationShifts");
        }

        return shifts;
    }

    /**
     * Sets the cross-location shifts assigned to the given home team members during given date-time interval.
     * @param params The parameters.
     * @param usersShifts The cross-location shifts assigned to the given home team members during given date-time interval.
     */
    public async setCrossLocationShifts(params: CrossLocationShiftDatabaseParams, usersShifts: UserBusyTimes[]): Promise<void> {
        try {
            const { endTimestampMs, startTimestampMs, teamId } = params;

            const records = usersShifts.reduce<CrossLocationShiftDbEntity[]>(
                (records, userShifts) => records.concat(this.createRecords(teamId, userShifts)),
                []
            );

            await this.database.crossLocationShifts
                .where({ teamId })
                .and(record => this.isRecordInInterval(startTimestampMs, endTimestampMs, record))
                .delete();

            await this.database.crossLocationShifts.bulkPut(records);
        } catch (error) {
            InstrumentationService.trackException(error, "CrossLocationShiftDatabase.setCrossLocationShifts");
        }
    }

    /**
     * Creates records from a single user cross-location shifts.
     * @param teamId The team id.
     * @param userShifts The user cross-location shifts.
     * @returns The records from a single user cross-location shifts.
     */
    private createRecords(teamId: string, userShifts: UserBusyTimes): CrossLocationShiftDbEntity[] {
        const { userId } = userShifts;

        return userShifts.busyTime.map(shift => ({
            endTimestampMs: this.createTimestampMs(shift.endDateTime),
            shift,
            startTimestampMs: this.createTimestampMs(shift.startDateTime),
            teamId,
            userId
        }));
    }

    /**
     * Creates a timestamp in milliseconds from a date-time string.
     * @param dateTime The date-time.
     * @returns The timestamp in milliseconds or 0 if date-time is invalid.
     */
    private createTimestampMs(dateTime: string): number {
        let timestampMs = 0;

        try {
            const date = new Date(dateTime);
            timestampMs = date.getTime();
        } catch (error) {
            InstrumentationService.trackException(error, "CrossLocationShiftDatabase.createTimestampMs");
        }

        return timestampMs;
    }

    /**
     * Creates shifts by users ids from given list of records.
     * @param records The records.
     * @returns Shifts by users ids.
     */
    private createShiftsByUsers(records: CrossLocationShiftDbEntity[]): { [userId: string]: ShiftBusyTime[] } {
        return records.reduce<{ [userId: string]: ShiftBusyTime[] }>((shiftsByUsers, record) => {
            const { shift, userId } = record;

            if (!(userId in shiftsByUsers)) {
                shiftsByUsers[userId] = [];
            }

            shiftsByUsers[userId].push(shift);
            return shiftsByUsers;
        }, {});
    }

    /**
     * Checks whether given record interval is within given interval.
     * @param startTimestampMs The start interval timestamp in milliseconds.
     * @param endTimestampMs The end interval timestamp in milliseconds.
     * @param record The record.
     * @returns Whether given record interval is within given interval.
     */
    private isRecordInInterval(startTimestampMs: number, endTimestampMs: number, record: CrossLocationShiftDbEntity): boolean {
        const startsInInterval = record.startTimestampMs >= startTimestampMs && record.startTimestampMs < endTimestampMs;
        const endsInInterval = record.endTimestampMs >= startTimestampMs && record.endTimestampMs <= endTimestampMs;

        return startsInInterval || endsInInterval;
    }
}
